// app/(app)/marketplace/MarketplaceView.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-hot-toast';
import { useAuth } from '@context/AuthContext';
import Layout from '@components/Layout';
import { db } from '@root/firebase';
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  increment,
  collection,
} from 'firebase/firestore';
import DOMPurify from 'dompurify';
import { logMarketplaceClick } from '@/utils/referrals';

export default function MarketplaceView() {
  const { id } = useParams();
  const { globalUser } = useAuth();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  // fetch item
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, 'marketplace', id);
        const snap = await getDoc(ref);
        if (!cancelled) {
          if (snap.exists()) {
            setItem({ id: snap.id, ...snap.data() });
          } else {
            toast.error('Item not found');
          }
        }
      } catch (e) {
        console.error(e);
        toast.error('Failed to load item.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => (cancelled = true);
  }, [id]);

  // record view once per user per day
  useEffect(() => {
    if (!item || !globalUser?.uid) return;
    const record = async () => {
      try {
        const parentRef = doc(db, 'marketplace', item.id);
        const viewsCol = collection(db, 'marketplace', item.id, 'views');
        const today = dayjs().format('YYYYMMDD');
        const viewDocRef = doc(viewsCol, `${globalUser.uid}_${today}`);

        await runTransaction(db, async (tx) => {
          const v = await tx.get(viewDocRef);
          if (!v.exists()) {
            tx.set(viewDocRef, {
              uid: globalUser.uid,
              dateKey: today,
              createdAt: serverTimestamp(),
            });
            tx.update(parentRef, { totalViews: increment(1) });
          }
        });
      } catch (e) {
        console.warn('record view failed', e);
      }
    };
    record();
  }, [item, globalUser?.uid]);

  const onVisitVendor = async (e) => {
    if (!item?.externalUrl) return;
    try {
      await logMarketplaceClick(item.id, globalUser?.uid, {
        product: item.product || item.title,
        externalUrl: item.externalUrl,
      });
      // allow the anchor to navigate normally
    } catch (err) {
      console.error('referral log failed', err);
    }
  };

  const safeDescription = useMemo(
    () => (item?.description ? DOMPurify.sanitize(item.description) : ''),
    [item?.description]
  );

  if (loading)
    return (
      <Layout>
        <p className='m-3'>Loading…</p>
      </Layout>
    );
  if (!item)
    return (
      <Layout>
        <p className='m-3'>Not found.</p>
      </Layout>
    );

  // Build a simple gallery: support single image/avatar or an images[] array if you have it
  const gallery =
    Array.isArray(item.images) && item.images.length
      ? item.images
      : [item.image || item.avatar].filter(Boolean);

  return (
    <Layout>
      {/* breadcrumbs */}
      <div className='row page-titles'>
        <div className='col-lg-8'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item'>
              <Link to='/app/marketplace'>Explore</Link>
            </li>
            <li className='breadcrumb-item'>Marketplace</li>
            <li className='breadcrumb-item active'>
              {item.product || item.title || 'Item'}
            </li>
          </ol>
        </div>
        <div className='col-lg-4' />
      </div>

      <div className='row'>
        <div className='col-lg-12'>
          <div className='card'>
            <div className='card-body'>
              <div className='row'>
                {/* Left: gallery */}
                <div className='col-xl-3 col-lg-6 col-md-6 col-xxl-5'>
                  {gallery.length ? (
                    <img
                      className='img-fluid rounded'
                      src={gallery[0]}
                      alt={item.product || item.title || 'Image'}
                    />
                  ) : (
                    <div className='text-muted'>No image</div>
                  )}
                  {/* If you want thumbnails, render gallery.slice(1) below */}
                </div>

                {/* Right: details */}
                <div className='col-xl-9 col-lg-6 col-md-6 col-xxl-7 col-sm-12'>
                  <div className='product-detail-content'>
                    <div className='new-arrival-content pr'>
                      <h3 className='mb-1'>
                        {item.product || item.title || 'Item'}
                      </h3>

                      <div className='d-table mb-2'>
                        {typeof item.price === 'number' && (
                          <p className='price float-start d-block fw-bold'>
                            ${item.price.toFixed(2)}
                          </p>
                        )}
                      </div>

                      <p className='mb-1'>
                        Category:{' '}
                        <span className='item'>
                          {item.categoryName}
                          {item.subCategoryName
                            ? ` - ${item.subCategoryName}`
                            : ''}
                        </span>
                      </p>

                      {item.productCode && (
                        <p className='mb-1'>
                          Product code:{' '}
                          <span className='item'>{item.productCode}</span>
                        </p>
                      )}

                      {item.brand && (
                        <p className='mb-1'>
                          Brand: <span className='item'>{item.brand}</span>
                        </p>
                      )}

                      {/* Full description */}
                      {safeDescription && (
                        <div
                          className='text-content mt-3'
                          dangerouslySetInnerHTML={{ __html: safeDescription }}
                        />
                      )}

                      {/* Vendor button */}
                      {item.externalUrl && (
                        <a
                          className='btn btn-primary mt-3'
                          href={item.externalUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          onClick={onVisitVendor}
                        >
                          Visit Vendor
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                {/* /Right */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
